import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import Database from '../../types/Database'

export type DiveCardProps = {
  dive: Database.Dive & { distanceKm?: number; hostHandle?: string | null }
  paymentStatus?: Database.JoinRequestPaymentStatus
  showDiveStatus?: boolean
}

const formatDateTime = (timestamp: { toDate: () => Date }): string => {
  const date = timestamp.toDate()
  return date.toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatDistance = (distanceKm: number | undefined | null): string => {
  if (distanceKm == null) return 'Distance unavailable'
  return `${Math.round(distanceKm)} km`
}

const formatPendingCount = (count: number | undefined | null): string => {
  if (count == null || count === 0) return '0'
  return String(count)
}

const DiveCard = ({ dive, paymentStatus, showDiveStatus }: DiveCardProps) => {
  return (
    <Card variant='outlined'>
      <CardActionArea>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 1
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant='subtitle1'
                component='h3'
                sx={{ fontWeight: 600 }}
              >
                {dive.siteName}
              </Typography>
              {dive.hostHandle && (
                <Typography variant='caption' color='text.secondary'>
                  @{dive.hostHandle}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {showDiveStatus && dive.status === 'cancelled' && (
                <Chip label='Cancelled' size='small' color='error' />
              )}
              <Chip
                label={dive.type === 'buddy' ? 'Buddy' : 'Guided'}
                size='small'
                color={dive.type === 'buddy' ? 'primary' : 'secondary'}
              />
            </Box>
          </Box>

          <Typography variant='body2' color='text.secondary' gutterBottom>
            {formatDateTime(dive.dateTime)}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mt: 1,
              flexWrap: 'wrap',
              alignItems: 'center'
            }}
          >
            <Typography variant='body2' color='text.secondary'>
              Depth: {dive.depthTier}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {formatDistance(dive.distanceKm)}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Requests: {formatPendingCount(dive.pendingRequestCount)}
            </Typography>
            {paymentStatus != null && dive.status !== 'cancelled' && (
              <Chip
                label={paymentStatus === 'paid' ? 'Paid' : 'Payment Due'}
                size='small'
                color={paymentStatus === 'paid' ? 'success' : 'warning'}
                variant='outlined'
              />
            )}
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

export default DiveCard
